import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import { HealthcareService, Location, Organization } from "fhir/r5";
import { NarrativeDisplay } from "@fyrstain/fhir-front-library";
import { Title } from "@fyrstain/hl7-front-library";
import { Badge, Button, Card } from "react-bootstrap";
import i18n from "i18next";
import HealthcareServiceService from "../../services/HealthcareServiceService";
import LocationService from "../../services/LocationService";
import OrganizationService from "../../services/OrganizationService";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
} from "../../utils/fhirReferences";
import {
  getChildHealthcareServices,
  getHealthcareServiceDisplayName,
  getHealthcareServiceKey,
  getParentHealthcareServices,
} from "../../utils/healthcareServiceTree";
import {
  getLocationKey,
} from "../../utils/locationTree";
import { resolveResourceIdByReference } from "../../utils/resourceLookup";
import { getOrganizationKey } from "../../utils/organizationTree";
import "./HealthcareServiceDetails.css";

function findHealthcareServiceByIdentifier(
  healthcareServices: HealthcareService[],
  healthcareServiceIdentifier: string
): HealthcareService | null {
  const normalizedIdentifier = healthcareServiceIdentifier.trim();

  if (!normalizedIdentifier) {
    return null;
  }

  return (
    healthcareServices.find((candidate) => {
      const candidateIdentifiers = (
        candidate.identifier ?? []
      ).flatMap((identifier) => [identifier.value ?? ""]);

      return (
        getHealthcareServiceKey(candidate) === normalizedIdentifier ||
        candidate.id?.trim() === normalizedIdentifier ||
        candidate.name?.trim() === normalizedIdentifier ||
        candidateIdentifiers.includes(normalizedIdentifier)
      );
    }) ?? null
  );
}

const HealthcareServiceDetails: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [fitFooter, setFitFooter] = useState(false);
  const { healthcareServiceId } = useParams();
  const [healthcareService, setHealthcareService] =
    useState<HealthcareService | null>(null);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [allHealthcareServices, setAllHealthcareServices] = useState<
    HealthcareService[]
  >([]);
  const [internalHealthcareServices, setInternalHealthcareServices] = useState<
    HealthcareService[]
  >([]);

  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [
        organizationsResponse,
        locationsResponse,
        healthcareServicesResponse,
        internalHealthcareServicesResponse,
      ] =
        await Promise.all([
          OrganizationService.loadAllOrganizations(),
          LocationService.loadAllLocations(),
          HealthcareServiceService.loadAllHealthcareServices(),
          HealthcareServiceService.loadAllInternalHealthcareServices(),
        ]);

      let serviceResponse: HealthcareService | null = null;

      try {
        serviceResponse = await HealthcareServiceService.loadHealthcareService(
          healthcareServiceId as string
        );
      } catch (error) {
        serviceResponse = findHealthcareServiceByIdentifier(
          healthcareServicesResponse,
          healthcareServiceId as string
        );
      }

      if (!serviceResponse) {
        onError();
        return;
      }

      setFitFooter(!serviceResponse.text?.div);
      setHealthcareService(serviceResponse);
      setAllOrganizations(organizationsResponse);
      setAllLocations(locationsResponse);
      setAllHealthcareServices(healthcareServicesResponse);
      setInternalHealthcareServices(internalHealthcareServicesResponse);
    } catch (error) {
      onError();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [healthcareServiceId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Derived data         //
  //////////////////////////////

  const primaryIdentifier =
    getOfficialIdentifierValue(healthcareService?.identifier) ||
    healthcareService?.id ||
    "N/A";

  const providerReferenceValue = getReferenceValue(
    healthcareService?.providedBy
  );
  const providerOrganization = allOrganizations.find((candidate) => {
    const candidateKey = getOrganizationKey(candidate);
    return (
      candidateKey === providerReferenceValue || candidate.id === providerReferenceValue
    );
  });

  const providerOrganizationTargetId =
    providerOrganization?.id ||
    resolveResourceIdByReference(allOrganizations, healthcareService?.providedBy);

  const serviceLocations = useMemo(() => {
    return (
      healthcareService?.location?.map((reference) => {
        const referenceValue = getReferenceValue(reference);
        const location = allLocations.find((candidate) => {
          const candidateKey = getLocationKey(candidate);
          return candidateKey === referenceValue || candidate.id === referenceValue;
        });

        return {
          reference,
          referenceValue,
          location,
        };
      }) ?? []
    );
  }, [allLocations, healthcareService]);

  const internalHealthcareServiceKeys = useMemo(
    () =>
      new Set(
        internalHealthcareServices
          .map((service) => getHealthcareServiceKey(service))
          .filter(Boolean)
      ),
    [internalHealthcareServices]
  );

  const parentHealthcareServices = useMemo(
    () =>
      getParentHealthcareServices(healthcareService, allHealthcareServices).filter(
        (service) =>
          internalHealthcareServiceKeys.size === 0 ||
          internalHealthcareServiceKeys.has(getHealthcareServiceKey(service))
      ),
    [allHealthcareServices, healthcareService, internalHealthcareServiceKeys]
  );

  const childHealthcareServices = useMemo(
    () =>
      getChildHealthcareServices(allHealthcareServices, healthcareService).filter(
        (service) =>
          internalHealthcareServiceKeys.size === 0 ||
          internalHealthcareServiceKeys.has(getHealthcareServiceKey(service))
      ),
    [allHealthcareServices, healthcareService, internalHealthcareServiceKeys]
  );

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const goToLocationDetails = useCallback(
    (locationId?: string | null) => {
      if (locationId) {
        navigate(`/Location/${encodeURIComponent(locationId)}`);
      }
    },
    [navigate]
  );

  const goToOrganizationDetails = useCallback(
    (organizationId?: string | null) => {
      if (organizationId) {
        navigate(`/Organization/${encodeURIComponent(organizationId)}`);
      }
    },
    [navigate]
  );

  const goToHealthcareServiceDetails = useCallback(
    (serviceId?: string | null) => {
      if (serviceId) {
        navigate(`/HealthcareService/${encodeURIComponent(serviceId)}`);
      }
    },
    [navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey="title.healthcareservicedetails"
      loading={loading}
      fitFooter={fitFooter}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title
              level={2}
              prefix={healthcareService?.resourceType}
              content={healthcareService?.id}
            />
          </Card.Header>
          <Card.Body>
            {healthcareService?.text?.div ? (
              <NarrativeDisplay
                resource={healthcareService}
                onError={onError}
                message={i18n.t("title.error.narrativenotfound")}
              />
            ) : (
              <div className="healthcare-service-details-layout">
                {healthcareService?.active !== undefined && (
                  <div className="healthcare-service-details-badges">
                    <Badge bg={healthcareService.active ? "success" : "danger"}>
                      {healthcareService.active
                        ? i18n.t("label.active")
                        : i18n.t("label.inactive")}
                    </Badge>
                  </div>
                )}
                <div className="healthcare-service-details-grid">
                  <div className="healthcare-service-detail-card">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.healthcareservicename")}
                    </span>
                    <span className="healthcare-service-detail-value">
                      {healthcareService?.name ?? "N/A"}
                    </span>
                  </div>
                  <div className="healthcare-service-detail-card">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.identifier")}
                    </span>
                    <span className="healthcare-service-detail-value">
                      {primaryIdentifier}
                    </span>
                  </div>
                  <div className="healthcare-service-detail-card">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.providedby")}
                    </span>
                    <span className="healthcare-service-detail-value">
                      {providerOrganizationTargetId ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() =>
                            goToOrganizationDetails(providerOrganizationTargetId)
                          }
                        >
                          {providerOrganization?.name ??
                            providerOrganization?.id ??
                            providerReferenceValue}
                        </Button>
                      ) : (
                        providerReferenceValue || "N/A"
                      )}
                    </span>
                  </div>
                  <div className="healthcare-service-detail-card healthcare-service-detail-card-full">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.locations")}
                    </span>
                    <div className="healthcare-service-detail-value healthcare-service-location-list">
                      {serviceLocations.length > 0 ? (
                        serviceLocations.map(({ referenceValue, location }) => {
                          const locationId = location?.id;
                          const label =
                            location?.name ??
                            locationId ??
                            referenceValue ??
                            "N/A";

                          return locationId ? (
                            <Button
                              key={locationId}
                              size="sm"
                              variant="outline-primary"
                              className="rounded-pill"
                              onClick={() => goToLocationDetails(locationId)}
                            >
                              {label}
                            </Button>
                          ) : (
                            <span key={referenceValue}>{label}</span>
                          );
                        })
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="healthcare-service-detail-card healthcare-service-detail-card-full">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.offeredin")}
                    </span>
                    <div className="healthcare-service-detail-value healthcare-service-location-list">
                      {parentHealthcareServices.length > 0 ? (
                        parentHealthcareServices.map((service) => (
                          <Button
                            key={service.id ?? getHealthcareServiceKey(service)}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() =>
                              goToHealthcareServiceDetails(
                                service.id ?? getHealthcareServiceKey(service)
                              )
                            }
                          >
                            {service.name ?? getHealthcareServiceDisplayName(service)}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="healthcare-service-detail-card healthcare-service-detail-card-full">
                    <span className="healthcare-service-detail-label">
                      {i18n.t("label.healthcareservices")}
                    </span>
                    <div className="healthcare-service-detail-value healthcare-service-location-list">
                      {childHealthcareServices.length > 0 ? (
                        childHealthcareServices.map((service) => (
                          <Button
                            key={service.id ?? getHealthcareServiceKey(service)}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() =>
                              goToHealthcareServiceDetails(
                                service.id ?? getHealthcareServiceKey(service)
                              )
                            }
                          >
                            {service.name ?? getHealthcareServiceDisplayName(service)}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default HealthcareServiceDetails;
